/**
 * Activity: the live event feed and the audit log.
 *
 * The feed is fed by Server-Sent Events over Postgres NOTIFY. Every line corresponds to a real
 * stored row and nothing here is synthesised — there is no fabricated activity (CLAUDE.md 3.3).
 *
 * When the stream delivers an event, the list re-fetches rather than optimistically appending
 * the notification payload. The database is the source of truth; the notification is only a
 * signal that it changed.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AuditEntry, type SixgenEvent } from '../api.js';

type Tab = 'events' | 'audit';

/** Renders an event as a sentence. Templates only — never invented detail. */
function describe(event: SixgenEvent): string {
  const payload = event.payload ?? {};
  switch (event.type) {
    case 'PROPERTY_CREATED':
      return `Property "${String(payload.name ?? 'unknown')}" was created.`;
    case 'PROPERTY_UPDATED': {
      const changed = Array.isArray(payload.changed) ? payload.changed.join(', ') : 'fields';
      return `A property was updated (${changed}).`;
    }
    case 'SYSTEM_HEARTBEAT':
      return 'System heartbeat — the event pipeline is alive.';
    case 'KILL_SWITCH_CHANGED':
      return 'The kill switch was changed.';
    default:
      return event.type;
  }
}

export function ActivityPage(): ReactNode {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('events');
  const [streamState, setStreamState] = useState<'connecting' | 'live' | 'offline'>('connecting');
  const [lastEventAt, setLastEventAt] = useState<Date | null>(null);

  const events = useQuery({
    queryKey: ['events'],
    queryFn: () => api.get<{ events: SixgenEvent[] }>('/api/events?limit=100'),
  });

  const audit = useQuery({
    queryKey: ['audit'],
    queryFn: () => api.get<{ entries: AuditEntry[] }>('/api/audit?limit=100'),
    enabled: tab === 'audit',
  });

  useEffect(() => {
    const source = new EventSource('/api/events/stream', { withCredentials: true });

    source.onopen = () => setStreamState('live');
    source.onerror = () => setStreamState('offline');
    source.addEventListener('sixgen_event', () => {
      setLastEventAt(new Date());
      // Re-fetch rather than trusting the notification payload as the record.
      void queryClient.invalidateQueries({ queryKey: ['events'] });
      void queryClient.invalidateQueries({ queryKey: ['audit'] });
    });

    return () => source.close();
  }, [queryClient]);

  const rows = events.data?.events ?? [];
  const auditRows = audit.data?.entries ?? [];

  return (
    <section>
      <div className="page-head">
        <div>
          <h1>Activity</h1>
          <p className="muted">
            Every line below is a stored row. Nothing on this page is generated for display.
          </p>
        </div>
        <span className={`stream stream-${streamState}`}>
          {streamState === 'live'
            ? 'Live'
            : streamState === 'connecting'
              ? 'Connecting…'
              : 'Offline'}
          {lastEventAt && streamState === 'live' && (
            <span className="muted small"> · last {lastEventAt.toLocaleTimeString()}</span>
          )}
        </span>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={tab === 'events' ? 'tab active' : 'tab'}
          onClick={() => setTab('events')}
        >
          Events
        </button>
        <button
          type="button"
          className={tab === 'audit' ? 'tab active' : 'tab'}
          onClick={() => setTab('audit')}
        >
          Audit log
        </button>
      </div>

      {tab === 'events' && (
        <>
          {events.isLoading && <p className="muted">Loading…</p>}
          {rows.length === 0 && !events.isLoading && (
            <div className="card empty">
              <p>No events yet.</p>
              <p className="muted small">
                Start the worker and a heartbeat arrives within a minute, or create a property.
              </p>
            </div>
          )}
          <ul className="feed">
            {rows.map((event) => (
              <li key={event.id}>
                <time dateTime={event.occurred_at}>
                  {new Date(event.occurred_at).toLocaleTimeString()}
                </time>
                <span className="feed-type">{event.type}</span>
                <span>{describe(event)}</span>
                {event.chain_depth > 0 && (
                  <span className="chip" title="This event was caused by another">
                    chain depth {event.chain_depth}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {tab === 'audit' && (
        <>
          {audit.isLoading && <p className="muted">Loading…</p>}
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Target</th>
                </tr>
              </thead>
              <tbody>
                {auditRows.map((entry) => (
                  <tr key={entry.id}>
                    <td className="small">{new Date(entry.occurred_at).toLocaleString()}</td>
                    <td className="small">
                      {entry.actor_type}
                      {entry.actor_id ? ` · ${entry.actor_id.slice(0, 8)}` : ''}
                    </td>
                    <td>
                      <code>{entry.action}</code>
                    </td>
                    <td className="small muted">
                      {entry.target_table}
                      {entry.target_id ? ` · ${entry.target_id.slice(0, 8)}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted small">
            The audit log is append-only, enforced by a database trigger. Nothing can edit or delete
            a row here.
          </p>
        </>
      )}
    </section>
  );
}
